<template>
  <input
    v-if="bare"
    ref="control"
    v-bind="$attrs"
    class="base-field__control base-field__control--bare"
    :type="type"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :inputmode="type === 'email' && inputmode === 'text' ? 'email' : inputmode"
    @input="onInput"
    @blur="emit('blur', $event)"
  />
  <label v-else class="base-field" :class="{ 'base-field--profile': profile, 'base-field--error': !!error, 'base-field--disabled': disabled }">
    <span v-if="label" class="base-field__label">{{ label }}</span>
    <input
      ref="control"
      class="base-field__control"
      :class="{ 'base-field__control--profile': profile }"
      v-bind="$attrs"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :inputmode="inputmode"
      @input="onInput"
      @blur="emit('blur', $event)"
    />
    <span v-if="error" class="base-field__msg base-field__msg--error">{{ error }}</span>
    <span v-else-if="hint" class="base-field__msg">{{ hint }}</span>
  </label>
</template>

<script setup lang="ts">
import { ref } from 'vue'

defineOptions({ inheritAttrs: false })
const control = ref<HTMLInputElement>()
defineExpose({ focus: (options?: Parameters<HTMLInputElement['focus']>[0]) => control.value?.focus(options) })

withDefaults(
  defineProps<{
    modelValue: string | number
    /** Preserve existing auth labels, errors and password wrappers. */
    bare?: boolean
    /** Use the same field appearance as the Profile form. */
    profile?: boolean
    label?: string
    hint?: string
    error?: string
    placeholder?: string
    type?: string
    disabled?: boolean
    inputmode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel' | 'search' | 'url'
  }>(),
  { label: '', hint: '', error: '', placeholder: '', type: 'text', disabled: false, inputmode: 'text' },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'blur', ev: FocusEvent): void
}>()

function onInput(ev: Event): void {
  emit('update:modelValue', (ev.target as HTMLInputElement).value)
}
</script>

<style scoped lang="scss">
@use '@/styles/profile-controls';

.base-field {
  display: flex;
  flex-direction: column;
  gap: var(--p-space-2);
  font-family: var(--p-font-ui);

  &__label {
    font-size: var(--p-text-14);
    font-weight: var(--p-weight-medium);
    color: var(--ds-text-secondary);
  }

  &__control {
    width: 100%;
    padding: var(--p-space-3) var(--p-space-4);
    font-size: var(--p-text-16);
    font-family: inherit;
    color: var(--ds-text);
    background: var(--ds-input-bg);
    border: 1px solid var(--ds-input-border);
    border-radius: var(--ds-input-radius);
    transition:
      border-color var(--p-motion-base) var(--p-ease),
      box-shadow var(--p-motion-base) var(--p-ease);

    &::placeholder {
      color: var(--ds-text-muted);
    }

    &:focus {
      outline: none;
      border-color: var(--ds-accent);
      box-shadow: var(--ds-focus-ring-shadow);
    }
  }

  &__msg {
    font-size: var(--p-text-12);
    color: var(--ds-text-muted);

    &--error {
      color: var(--ds-danger);
    }
  }

  &__control.base-field__control--bare {
    color: var(--ds-text) !important;
    background: var(--ds-surface-sunken) !important;
    border-color: var(--ds-border) !important;
    box-shadow: none !important;

    &:focus {
      border-color: var(--ds-text) !important;
      outline: 2px solid var(--ds-text);
      outline-offset: 2px;
    }

    &:disabled {
      opacity: .6;
      cursor: not-allowed;
    }

    &.form-input--error, &.input-error {
      border-color: var(--ds-danger) !important;
    }
  }

  &__control--profile {
    @include profile-controls.input;
  }

  &--profile {
    gap: 6px;
  }

  &--profile &__label {
    font-size: var(--p-text-13);
    font-weight: var(--ds-weight-medium);
    color: var(--hm-text-secondary);
  }

  &--error &__control {
    border-color: var(--ds-danger);
  }

  &--disabled {
    opacity: 0.6;
  }
}
</style>
